import React, { useState } from "react";
import { 
  ArrowLeft, 
  Star, 
  ChevronDown, 
  ChevronUp, 
  Wifi, 
  Settings, 
  Monitor,
  CheckCircle,
  AlertTriangle,
  Play,
  Network
} from "lucide-react";

const CameraSetup = ({ selectedCamera = null }) => {
  const [expandedFaq, setExpandedFaq] = useState(null);
  
  // Demo camera data if none provided
  const demoCamera = {
    id: "cam-001",
    name: "AI Vision Pro 4K",
    type: "Indoor/Outdoor",
    resolution: "4K Ultra HD",
    fps: "30 FPS",
    ai_capability: "Advanced",
    rating: 4.8,
    features: ["Night Vision", "Weather Resistant", "Edge AI Processing", "Real-time Analytics"],
    best_for: ["Person Detection", "PPE Detection", "Face Recognition", "Movement Tracking"],
    specs: {
      sensor: "1/2.8\" CMOS",
      lens: "2.8-12mm varifocal",
      ir_range: "100m",
      storage: "Local + Cloud",
    },
    image: "",
  };

  const camera = selectedCamera || demoCamera;

  const faqData = [
    {
      id: 1,
      question: "What camera software do I need to install?",
      answer: "You'll need to install the camera manufacturer's official software or IP camera viewing software like VLC, IP Camera Viewer, or the manufacturer's dedicated app. For this camera, we recommend using the Smart Vision Pro software that comes with the package."
    },
    {
      id: 2,
      question: "How do I connect the camera via Ethernet cable?",
      answer: "1. Connect one end of the Ethernet cable to the camera's network port\n2. Connect the other end to your router, switch, or directly to your computer\n3. Ensure the network LED indicator on the camera lights up (usually green/blue)\n4. Wait 30-60 seconds for the camera to boot up completely"
    },
    {
      id: 3,
      question: "How do I find and set the camera's IP address?",
      answer: "1. Use IP scanning software like Advanced IP Scanner or the manufacturer's discovery tool\n2. Check your router's connected devices list\n3. Default IP is usually 192.168.1.64 or 192.168.0.64\n4. Access the camera's web interface by typing the IP in your browser\n5. Set a static IP within your network range (e.g., 192.168.1.100)"
    },
    {
      id: 4,
      question: "What should I check to verify the camera is streaming?",
      answer: "1. Green network indicator light on the camera\n2. Camera appears in network scanning results\n3. Can access camera's web interface via IP address\n4. Live video feed displays without errors\n5. Can adjust camera settings (pan, tilt, zoom if applicable)\n6. Audio is working (if camera supports it)"
    },
    {
      id: 5,
      question: "What are the default login credentials?",
      answer: "Default credentials are usually:\nUsername: admin\nPassword: admin or 123456\n\nIMPORTANT: Change these immediately after first login for security. Check the camera label or manual for specific defaults."
    },
    {
      id: 6,
      question: "Network requirements and port settings",
      answer: "Required ports:\n- HTTP: Port 80\n- RTSP: Port 554\n- HTTPS: Port 443 (for secure access)\n\nEnsure your firewall allows these ports. For remote access, configure port forwarding on your router."
    },
    {
      id: 7,
      question: "Troubleshooting connection issues",
      answer: "If camera doesn't connect:\n1. Check cable connections and LED indicators\n2. Try power cycling the camera (unplug for 10 seconds)\n3. Verify network settings and IP range\n4. Disable firewall temporarily to test\n5. Use ping command to test connectivity\n6. Reset camera to factory defaults if needed"
    },
    {
      id: 8,
      question: "Optimal camera positioning and setup",
      answer: "1. Mount at 8-10 feet height for optimal coverage\n2. Avoid direct sunlight or bright lights in view\n3. Ensure stable mounting to prevent vibrations\n4. Check cable routing for weatherproofing\n5. Test different angles for best detection coverage\n6. Consider lighting conditions for night vision"
    }
  ];

  const handleBack = () => {
  };

  const moveToHome = () => {
  };

  const handleFaqToggle = (id) => {
    setExpandedFaq(expandedFaq === id ? null : id);
  };

  const handleConnect = () => {
    alert("Camera connection initiated! This would typically open the camera management interface.");
  };

  return (
    <div className="min-h-screen bg-gray-50 relative">
      {/* Background */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none opacity-30">
        <div className="absolute top-0 left-0 w-64 h-64 bg-gradient-to-br from-blue-100/20 to-cyan-100/20 rounded-full blur-2xl"></div>
        <div className="absolute bottom-0 right-0 w-64 h-64 bg-gradient-to-br from-green-100/20 to-emerald-100/20 rounded-full blur-2xl"></div>
      </div>

      <div className="relative w-full px-4 sm:px-6 lg:px-8 pt-4 pb-12">
        {/* Breadcrumb */}
        <nav className="text-sm p-4 text-blue-600 font-medium tracking-wider">
          <span className="cursor-pointer hover:text-blue-800" onClick={moveToHome}>Admin Page</span>
          <span className="mx-2 text-gray-400">/</span>
          <span className="cursor-pointer hover:text-blue-800" onClick={() => console.log("Going to safety page")}>Safety</span>
          <span className="mx-2 text-gray-400">/</span>
          <span className="cursor-pointer hover:text-blue-800" onClick={handleBack}>Select Camera</span>
          <span className="mx-2 text-gray-400">/</span>
          <span className="text-gray-600">Camera Setup</span>
        </nav>

        {/* Back Button */}
        <button
          onClick={handleBack}
          className="mb-6 flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors duration-300"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Camera Selection
        </button>

        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Camera Setup & Configuration</h1>
          <p className="text-gray-600">Complete setup guide for your selected camera</p>
        </div>

        {/* Selected Camera Display */}
        <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
          <div className="flex items-center gap-4 mb-4">
            <CheckCircle className="w-6 h-6 text-green-500" />
            <h2 className="text-xl font-semibold text-gray-900">Selected Camera</h2>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Camera Image */}
            <div className="space-y-4">
              <div className="relative bg-gray-100 rounded-lg aspect-video flex items-center justify-center overflow-hidden">
                {camera.image ? (
                  <img 
                    src={camera.image} 
                    alt={camera.name}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="text-center">
                    <Monitor className="w-16 h-16 text-gray-400 mx-auto mb-2" />
                    <p className="text-gray-500">{camera.name}</p>
                  </div>
                )}
                <div className="absolute top-4 right-4 bg-white/90 backdrop-blur-sm rounded-full px-3 py-1 flex items-center gap-1">
                  <Star className="w-4 h-4 text-yellow-500 fill-current" />
                  <span className="text-sm font-medium">{camera.rating}</span>
                </div>
              </div>
            </div>

            {/* Camera Details */}
            <div className="space-y-6">
              <div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">{camera.name}</h3>
                <p className="text-gray-600 mb-4">{camera.type} • {camera.ai_capability}</p>
                
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div className="bg-blue-50 p-3 rounded-lg">
                    <p className="text-sm text-blue-600 font-medium">Resolution</p>
                    <p className="text-lg font-semibold text-blue-900">{camera.resolution}</p>
                  </div>
                  <div className="bg-green-50 p-3 rounded-lg">
                    <p className="text-sm text-green-600 font-medium">Frame Rate</p>
                    <p className="text-lg font-semibold text-green-900">{camera.fps}</p>
                  </div>
                </div>
              </div>

              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Key Features:</h4>
                <div className="flex flex-wrap gap-2 mb-4">
                  {camera.features.map((feature) => (
                    <span key={feature} className="px-3 py-1 bg-blue-100 text-blue-800 text-sm rounded-full">
                      {feature}
                    </span>
                  ))}
                </div>
              </div>

              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Optimized For:</h4>
                <div className="flex flex-wrap gap-2">
                  {camera.best_for.map((capability) => (
                    <span key={capability} className="px-3 py-1 bg-green-100 text-green-800 text-sm rounded-full">
                      {capability}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* FAQ Section */}
        <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
          <div className="flex items-center gap-4 mb-6">
            <Settings className="w-6 h-6 text-blue-500" />
            <h2 className="text-xl font-semibold text-gray-900">Camera Setup Guide & FAQ</h2>
          </div>

          <div className="space-y-4">
            {faqData.map((faq) => (
              <div key={faq.id} className="border border-gray-200 rounded-lg">
                <button
                  onClick={() => handleFaqToggle(faq.id)}
                  className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-gray-50 transition-colors duration-200"
                >
                  <span className="font-medium text-gray-900">{faq.question}</span>
                  {expandedFaq === faq.id ? (
                    <ChevronUp className="w-5 h-5 text-gray-500" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-gray-500" />
                  )}
                </button>
                
                {expandedFaq === faq.id && (
                  <div className="px-6 pb-4">
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <pre className="text-gray-700 text-sm whitespace-pre-wrap font-sans">
                        {faq.answer}
                      </pre>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Connection Status & Connect Button */}
        <div className="bg-white rounded-xl shadow-lg p-6">
          <div className="text-center">
            <div className="flex items-center justify-center gap-4 mb-6">
              <Network className="w-8 h-8 text-blue-500" />
              <h2 className="text-xl font-semibold text-gray-900">Ready to Connect</h2>
            </div>
            
            <p className="text-gray-600 mb-6 max-w-2xl mx-auto">
              Follow the setup guide above to configure your camera. Once you've completed the initial setup 
              and verified the camera is streaming, click the connect button below to integrate it with your system.
            </p>

            <div className="flex items-center justify-center gap-4 mb-6">
              <div className="flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-amber-500" />
                <span className="text-sm text-gray-600">Ensure camera is powered and connected to network</span>
              </div>
            </div>

            <button
              onClick={handleConnect}
              className="inline-flex items-center gap-3 px-8 py-4 bg-gradient-to-r from-blue-500 to-blue-600 text-white font-semibold rounded-lg hover:from-blue-600 hover:to-blue-700 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105"
            >
              <Wifi className="w-5 h-5" />
              Connect Camera to System
              <Play className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CameraSetup;